<template>
    <Container
        v-if="hasCachedPosts || hasLoadedPosts"
        class="tw-pb-10">
        <CategoriesWrapper class="tw-sticky tw-top-safe-area-top tw-z-10 tw-mb-4 tw-bg-white tw-py-2">
            <CategoryButton
                v-for="category in filteredCategories"
                :key="category.value"
                :active="routeCategory.value === category.value"
                :category="category"
                :to="{
                    name: 'News',
                    params: { category: category.value },
                }">
                <CategoryName :category="category" />
            </CategoryButton>
        </CategoriesWrapper>
        <template v-if="routeCategory.value === 'opinion'">
            <OpinionPage :hide-container="true" />
        </template>
        <template v-else-if="hasPosts || firstPost">
            <HighlightedPost
                v-if="firstPost"
                :category="routeCategory"
                class="tw-mb-5"
                :post="firstPost" />

            <PostList
                v-if="allPosts.length > 0"
                :category="routeCategory"
                :posts="allPosts"
                show-ads
                @fetch-more="fetchMore">
            </PostList>

            <SeeMoreButton
                v-if="allPosts.length < totalPosts"
                class="tw-mx-auto tw-mt-5"
                :loading="isLoadingPage"
                @click="fetchMore" />
        </template>
        <template v-else-if="forceLoading">
            <HighlightedPostLoader></HighlightedPostLoader>
            <template
                v-for="i in 4"
                :key="i">
                <PostListItemLoader></PostListItemLoader>
                <v-divider
                    v-if="i < 4"
                    class="tw-my-5"
                    opacity="0.1"></v-divider>
            </template>
        </template>
        <div
            v-else
            class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-8">
            <NewsIcon class="tw-size-24 tw-text-gray-300" />
            <span class="tw-max-w-48 tw-text-center tw-text-gray-400">
                {{ $t('News.noNews') }}
            </span>
        </div>
    </Container>
    <Loader v-else />
</template>

<script setup lang="ts">
    import HighlightedPostLoader from '@/components/loaders/news/HighlightedPostLoader.vue';
    import NewsIcon from '@/components/icons/News.vue';
    import Loader from './loader.vue';
    import { getPostsFromCache } from '@/composables/usePost';
    import categories, { type Category } from '@/configs/categories';
    import type { WPPost } from '@/contentTypes';
    import { fetchPosts } from '@/api/posts';
    import _ from 'lodash';
    import { POSTS_PER_PAGE } from '@/configs/posts';
    import { useAppStore } from '@/store/app';
    import { PAGINATION_CONSTANTS } from '@/configs/constants';
    import OpinionPage from '@/views/opinion/page.vue';

    const hasLoadedPosts = inject('hasLoadedPosts', ref(false));
    const appStore = useAppStore();
    const $route = useRoute();
    const $router = useRouter();

    const filteredCategories = categories.filter((category) => category.showInNews);

    const routeCategory = computed(() => {
        return categories.find((category) => category.value === $route.params.category) || categories[0];
    });

    const hasCachedPosts = ref(false);
    const isLoadingPage = ref(false);
    const forceLoading = ref(false);

    const firstPost = ref<WPPost | null>(null);
    const cachedPosts = ref<WPPost[]>([]);
    const posts = ref<WPPost[]>([]);
    const postsPerCategory = ref({});

    const page = ref(1);

    const hasPosts = computed(() => {
        return allPosts.value.length > 0;
    });

    const totalPosts = computed(() => {
        const total = appStore.getPostsTotalByCategory(routeCategory.value.wordpressId) || 0;
        return total - 1;
    });

    // join posts and cachedPosts
    const allPosts = computed(() => {
        return [...cachedPosts.value, ...posts.value];
    });

    async function fetchMore() {
        if (isLoadingPage.value || allPosts.value.length >= totalPosts.value) {
            return;
        }
        page.value++;
        isLoadingPage.value = true;

        const query = {
            page: page.value, // Increment page number
            per_page: POSTS_PER_PAGE,
            categories: routeCategory.value.wordpressId,
        };
        try {
            const { data } = await fetchPosts(query);
            if (data && data[`_${routeCategory.value.wordpressId}`]) {
                posts.value.push(...data[`_${routeCategory.value.wordpressId}`].posts);
            }
        } catch (error) {
            console.error('Error fetching more posts:', error);
        } finally {
            isLoadingPage.value = false;
        }
    }

    async function init() {
        forceLoading.value = true;
        const postsData = await getPostsFromCache({
            category: routeCategory.value.wordpressId,
            pageSize: POSTS_PER_PAGE,
        });
        if (!postsData || postsData.length === 0) {
            firstPost.value = null;
            posts.value = [];
            return;
        }

        hasCachedPosts.value = true;
        // first post
        firstPost.value = postsData.length > 0 ? postsData[0] : null;

        // all posts
        cachedPosts.value = postsData.slice(1); // Exclude the first post

        if (posts.value.length > 0) {
            // check if the cached and first post are in the posts, if they are, remove them from the posts
            const cachedPostIds = new Set(cachedPosts.value.map((post) => post.id));
            const firstPostId = firstPost.value?.id;
            posts.value = posts.value.filter((post) => {
                return !cachedPostIds.has(post.id) && post.id !== firstPostId;
            });
        }
        forceLoading.value = false;
    }

    watch(routeCategory, (newCategory: Category, oldCategory: Category) => {
        _.set(postsPerCategory.value, oldCategory.value, {
            cachedPosts: cachedPosts.value,
            firstPost: firstPost.value,
            posts: posts.value,
            page: page.value,
        });

        // scroll to top
        // window.scrollTo({ top: 0, behavior: 'smooth' });
        page.value = _.get(postsPerCategory.value, `${newCategory.value}.page`, 1);
        posts.value = _.get(postsPerCategory.value, `${newCategory.value}.posts`, []);
        cachedPosts.value = _.get(postsPerCategory.value, `${newCategory.value}.cachedPosts`, []);
        firstPost.value = _.get(postsPerCategory.value, `${newCategory.value}.firstPost`, null);
        if (!cachedPosts.value.length) init();
    });

    init();

    watch(hasLoadedPosts, async (newValue) => {
        if (newValue) {
            init();
        }
    });
</script>

<style scoped></style>
